
import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formState);
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after submission
      setFormState({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
      
      // Reset success message after a delay
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };

  return (
    <section className="section-padding bg-pkstone-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 animate-fade-up">
          <span className="text-pkgreen-600 font-display uppercase tracking-wider text-sm mb-2 block">
            Get In Touch
          </span>
          <h2 className="section-title">Contact Us</h2>
          <p className="max-w-3xl mx-auto text-pkstone-700 text-lg">
            Ready to transform your landscape? Contact us to schedule a consultation
            or request a quote for your project.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-1 animate-fade-up">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-serif font-bold mb-6 text-pkstone-900">
                Contact Information
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-pkgreen-100 p-3 rounded-full mr-4">
                    <Phone className="h-5 w-5 text-pkgreen-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-pkstone-900 mb-1">Phone</h4>
                    <a 
                      href="tel:+12034453594" 
                      className="text-pkstone-600 hover:text-pkgreen-600 transition-colors"
                    >
                      (203) 445-3594
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-pkgreen-100 p-3 rounded-full mr-4">
                    <Mail className="h-5 w-5 text-pkgreen-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-pkstone-900 mb-1">Email</h4>
                    <a 
                      href="mailto:hello@pklandscapect.com" 
                      className="text-pkstone-600 hover:text-pkgreen-600 transition-colors"
                    >
                      hello@pklandscapect.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-pkgreen-100 p-3 rounded-full mr-4">
                    <MapPin className="h-5 w-5 text-pkgreen-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-pkstone-900 mb-1">Location</h4>
                    <address className="text-pkstone-600 not-italic">
                      610 Brooklawn Avenue<br />
                      Bridgeport, CT 06604
                    </address>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-8 border-t border-pkstone-100">
                <h4 className="font-medium text-pkstone-900 mb-3">Service Areas</h4>
                <p className="text-pkstone-600 mb-4">
                  Proudly serving Fairfield County including Greenwich, Stamford, 
                  Darien, Westport, Fairfield, and surrounding areas.
                </p>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-2 animate-fade-up">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-serif font-bold mb-6 text-pkstone-900">
                Send Us a Message
              </h3>
              
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-100 rounded-lg p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <CheckCircle className="h-12 w-12 text-green-500" />
                  </div>
                  <h4 className="text-xl font-medium text-green-800 mb-2">
                    Thank You!
                  </h4>
                  <p className="text-green-700">
                    Your message has been received. We'll get back to you shortly!
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-pkstone-700 mb-2 font-medium">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formState.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkgreen-500 focus:ring-2 focus:ring-pkgreen-200 transition-all duration-300 outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-pkstone-700 mb-2 font-medium">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkgreen-500 focus:ring-2 focus:ring-pkgreen-200 transition-all duration-300 outline-none"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-pkstone-700 mb-2 font-medium">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formState.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkgreen-500 focus:ring-2 focus:ring-pkgreen-200 transition-all duration-300 outline-none"
                        placeholder="(123) 456-7890"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="service" className="block text-pkstone-700 mb-2 font-medium">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formState.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkgreen-500 focus:ring-2 focus:ring-pkgreen-200 transition-all duration-300 outline-none"
                      >
                        <option value="">Select a service</option>
                        <option value="design">Landscape Design</option>
                        <option value="installation">Landscape Installation</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="snow">Snow Removal</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-pkstone-700 mb-2 font-medium">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkgreen-500 focus:ring-2 focus:ring-pkgreen-200 transition-all duration-300 outline-none"
                      placeholder="Tell us about your project or request..."
                    ></textarea>
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        "w-full md:w-auto bg-pkgreen-600 hover:bg-pkgreen-700 text-white font-medium py-3 px-8 rounded-md inline-flex items-center justify-center transition-all duration-300 ease-smooth",
                        isSubmitting && "opacity-70 cursor-not-allowed"
                      )}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
